import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import ErrorComponent from '@/components/ui/custom/ErrorComponent';
import InfinityList from '@/components/ui/custom/InfinityList';
import Card from '@/components/projects/cards/Card';
import ProjectCardsFactory from '@/components/projects/cards/factory/ProjectCardsFactory';
import CourseTypeFilter from '@/components/projects/carousel/filters/CourseTypeFilter';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { ProjectFilters } from '@/data/services/projectServices';
import { projectsQuerykeys } from '@/data/services/querykeys';
import { spring } from '@/utils/animations/commom';
import { RectangleStackIcon } from '@heroicons/react/24/outline';
import { motion } from 'motion/react';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import HackathonBanner from '../../../components/projects/hackathon/HackathonBanner/HackathonBanner';

import LoadingView from '../../courses/LoadingView';
import ProjectSectionHeaderPage from '../ProjectSectionHeaderPage';
import useHackathon from '@/data/hook/hackathon/useHackathon';
import useHackathonFilters from '@/data/hook/hackathon/useHackathonFilters';
import useManualQuery from '@/data/hook/query/v1/useManualQuery';
import withLeiFelca from '@/components/projects/withLeiFelca';

function ProjectHackathonPage() {
  const { filters: headerFilters } = useOutletContext<{
    filters: ProjectFilters;
  }>();

  const {
    hackathon,
    hackathonsStages,
    isLoading: isLoadingHackathon,
  } = useHackathon();

  const { t } = useTranslation('translation', {
    keyPrefix: 'projects.page',
  });
  const { t: tHackathon } = useTranslation('translation', {
    keyPrefix: 'hackathon',
  });
  const [sectionFilters, setSectionFilters] = useState({});

  const hackathonFilters = useHackathonFilters();

  const allFilters = {
    ...headerFilters,
    ...sectionFilters,
    ...hackathonFilters,
  };

  const { queryFn, queryKey } =
    projectsQuerykeys.list(allFilters)._ctx.infinity;
  const {
    results: projects,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
    isFetching,
    isLoading,
  } = useInfiniteService({
    queryFn,
    queryKey,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const { update } = useManualQuery({ queryKey });

  const loadingCards = Array.from({ length: 20 }).map((_, index) => (
    <Card.Skeleton key={index} className="w-full" />
  ));

  if (isLoadingHackathon) {
    return <LoadingView />;
  }

  if (!hackathon && !isLoadingHackathon) {
    return (
      <ErrorComponent
        statusCode={404}
        errorTextTitle={t('hackathon')}
        errorTextSubTitle={t('noHackathon')}
      />
    );
  }

  if (hackathon) {
    return (
      <div className="flex flex-col gap-4">
        <ProjectSectionHeaderPage
          title={`#${t('hackathon')}${hackathon?.edition}`}
          hiddenFilter
          className={{
            title: '!text-primary text-20',
          }}
        />
        <HackathonBanner
          hackathon={hackathon}
          hackathonStages={hackathonsStages}
        />
        <div className="flex justify-between w-full items-center">
          <CourseTypeFilter
            size="medium"
            filter={sectionFilters}
            onFilter={setSectionFilters}
          />
          <ConditionalRenderer condition={hackathonFilters.shunfle}>
            <MainButton
              text={tHackathon('shunfle')}
              loading={isFetching}
              color="custom"
              onClick={() => refetch()}
              className="font-600 !text-14 text-primary gap-1 flex-row-reverse"
              icon={
                <RectangleStackIcon
                  className={'w-4 h-4 ease-in-out transition-all duration-200'}
                />
              }
            />
          </ConditionalRenderer>
        </div>
        <ConditionalRenderer condition={!projects.length && !isLoading}>
          <div className="flex w-full justify-center items-center h-72">
            <Text text={t('noProjectsFound')} />
          </div>
        </ConditionalRenderer>
        <InfinityList
          hasNextPage={hasNextPage}
          onReachEnd={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
          className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2.5"
        >
          <ConditionalRenderer condition={!isLoading} fallback={loadingCards}>
            {projects.map(project => (
              <motion.div
                key={project.id}
                layout
                transition={spring}
                className="w-full min-w-full xl:min-w-full"
              >
                <ProjectCardsFactory
                  project={project}
                  onMutate={update}
                  mode="hackathon"
                  className={{
                    base: 'w-full min-w-full xl:min-w-full',
                  }}
                />
              </motion.div>
            ))}
          </ConditionalRenderer>
        </InfinityList>
      </div>
    );
  }
  return <Fragment />;
}

export default withLeiFelca(ProjectHackathonPage);
